/**
 * ComplaintAnalyticsDashboard - Trends, resolution metrics, top categories
 */
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import {
  TrendingUp, Clock, CheckCircle, AlertTriangle, BarChart3,
  Loader2, Calendar, Target, Shield, Sparkles, Search
} from "lucide-react";

const COLORS = ['#2F4F3A', '#B4845C', '#6B8F71', '#D4A574', '#4A7C59', '#C9956B', '#3D6B4F', '#8B6F47'];

const STATUS_COLORS = {
  open: '#3b82f6',
  under_review: '#eab308',
  investigation: '#8b5cf6',
  verified: '#22c55e',
  unverified: '#6b7280',
  escalated: '#ef4444',
  closed: '#64748b'
};

const CATEGORY_LABELS = {
  neighbor_violation: "Neighbor Violation",
  architectural: "Architectural/DRC",
  board_conduct: "Board Conduct",
  safety: "Safety",
  nuisance: "Nuisance",
  maintenance: "Maintenance",
  financial: "Financial",
  property_manager: "Property Manager",
  other: "Other"
};

const ComplaintAnalyticsDashboard = ({ embedded = false }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState("12");

  useEffect(() => {
    if (token) fetchAnalytics();
  }, [token, months]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/complaints/analytics/overview?months=${months}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    if (embedded) return <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    return (
      <div className="flex min-h-screen bg-background">
        <AdminSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  const { summary, monthly_trend, by_category, by_status, resolution_distribution } = data;

  const categoryPieData = by_category.map(c => ({
    name: CATEGORY_LABELS[c.category] || c.category,
    value: c.count
  }));

  const statusPieData = by_status.map(s => ({
    name: s.status.replace(/_/g, ' '),
    value: s.count,
    fill: STATUS_COLORS[s.status] || '#6b7280'
  }));

  const analyticsContent = (
      <main className="flex-1 p-6" data-testid="complaint-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-serif">Complaint Analytics</h1>
            <p className="text-muted-foreground">Trends, resolution metrics, and enforcement effectiveness</p>
          </div>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-40" data-testid="analytics-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card data-testid="kpi-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground">Total</span>
              </div>
              <div className="text-2xl font-bold">{summary.total}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-open">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Open</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">{summary.open}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-closed">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Closed</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{summary.closed}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-avg-resolution">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-muted-foreground">Avg Resolution</span>
              </div>
              <div className="text-2xl font-bold">{summary.avg_resolution_days}d</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-ai-analysis">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span className="text-xs text-muted-foreground">AI Analyzed</span>
              </div>
              <div className="text-2xl font-bold text-purple-600">{summary.with_ai_analysis}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-investigations">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Search className="w-4 h-4 text-indigo-500" />
                <span className="text-xs text-muted-foreground">Investigated</span>
              </div>
              <div className="text-2xl font-bold text-indigo-600">{summary.with_investigation}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="trends" className="space-y-4">
          <TabsList>
            <TabsTrigger value="trends">Monthly Trends</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="resolution">Resolution Time</TabsTrigger>
            <TabsTrigger value="status">Status Breakdown</TabsTrigger>
          </TabsList>

          {/* Monthly Trends */}
          <TabsContent value="trends">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Complaints by Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={monthly_trend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="opened" fill="#2F4F3A" name="Opened" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="closed" fill="#B4845C" name="Closed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories */}
          <TabsContent value="categories">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Complaints by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={categoryPieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={130}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={true}
                      >
                        {categoryPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {by_category.slice(0, 8).map((cat, i) => (
                      <div key={cat.category} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="flex-1 text-sm">{CATEGORY_LABELS[cat.category] || cat.category}</span>
                        <Badge variant="outline">{cat.count}</Badge>
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${(cat.count / (by_category[0]?.count || 1)) * 100}%`,
                              backgroundColor: COLORS[i % COLORS.length]
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Resolution Time */}
          <TabsContent value="resolution">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Resolution Time Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={resolution_distribution} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6B8F71" name="Complaints" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resolution Metrics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 pt-4">
                  <div className="text-center p-6 bg-green-50 rounded-xl">
                    <div className="text-4xl font-bold text-green-700">{summary.avg_resolution_days}</div>
                    <div className="text-sm text-green-600 mt-1">Average Days to Resolution</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold">{summary.min_resolution_days}</div>
                      <div className="text-xs text-muted-foreground">Fastest (days)</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold">{summary.max_resolution_days}</div>
                      <div className="text-xs text-muted-foreground">Slowest (days)</div>
                    </div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-xl font-bold text-purple-700">
                      {summary.total > 0 ? Math.round((summary.closed / summary.total) * 100) : 0}%
                    </div>
                    <div className="text-xs text-purple-600">Resolution Rate</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Status Breakdown */}
          <TabsContent value="status">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Current Status Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={130}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {statusPieData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">AI & Investigation Usage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        AI Analysis Rate
                      </span>
                      <span className="font-bold text-purple-700">
                        {summary.total > 0 ? Math.round((summary.with_ai_analysis / summary.total) * 100) : 0}%
                      </span>
                    </div>
                    <div className="w-full h-3 bg-purple-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${summary.total > 0 ? (summary.with_ai_analysis / summary.total) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {summary.with_ai_analysis} of {summary.total} complaints analyzed by AI
                    </div>
                  </div>
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Search className="w-4 h-4 text-indigo-600" />
                        Investigation Rate
                      </span>
                      <span className="font-bold text-indigo-700">
                        {summary.total > 0 ? Math.round((summary.with_investigation / summary.total) * 100) : 0}%
                      </span>
                    </div>
                    <div className="w-full h-3 bg-indigo-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all"
                        style={{ width: `${summary.total > 0 ? (summary.with_investigation / summary.total) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {summary.with_investigation} of {summary.total} complaints investigated
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
  );

  if (embedded) return analyticsContent;
  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      {analyticsContent}
    </div>
  );
};

export default ComplaintAnalyticsDashboard;
